import {
  MULTI_VALUED,
  RESPONSE_SHAPE,
  RULE_KINDS,
  Rule,
  TEXT_CONTROLS,
  type Control,
  type DraftField,
  type MultiValued,
  type RuleKind,
} from '@eformulieren/model'

/**
 * Welke regelsoort op welk doel past (D-14, ontwerp 8.5).
 *
 * **Of een soort een veld of een sectie beheerst, is afgeleid uit de unie en niet opgeschreven.**
 * Draagt zijn tak van `Regel` `doelVeld`, dan gaat hij over een veld; draagt hij `doelSectie`, dan
 * over een sectie; `zichtbaarBij` draagt `doel` en kan allebei (R-33). Een tweede lijst naast
 * `RULE_KINDS` zou achterlopen zodra er een soort bijkomt, en dat is precies wat D-47 uitsluit.
 *
 * **Welke veldsoort op welk controltype past, staat hier, en de poort leest het ook hier**
 * (besluit van 31 oktober 2026, eisenboek 12.29). Tot die dag stond hier dat verder inperken dan het
 * schema beleid zou zijn dat niemand had vastgesteld, en bood het paneel een datumbereik aan op een
 * tekstveld en bestandseisen op een keuzeveld. Die regels deden niets of hielden indienen tegen zonder
 * dat de invuller kon zien waarom. Nu volgt de inperking uit wat de evaluator in `check.ts` van het
 * antwoord leest: een teken- of woordgrens leest tekst, een getalsbereik een getal, een datumbereik een
 * datum, bestandseisen documenten, een aantal een verzameling. De tabel staat in `FITS` en is een
 * `Record`, zodat een soort die erbij komt de compilatie laat omvallen in plaats van stil overal of
 * nergens te passen.
 *
 * **Waarom in de poort en niet in de superRefine van `Definitie`.** De opslagdienst leest bewaarde
 * publicaties met `Definitie.parse`; een strenger schema zou een publicatie van vóór dit besluit
 * onleesbaar maken (D-36). De poort kijkt alleen naar wat nu wordt gepubliceerd (`fitGaps` in
 * `publicatieCheck.ts`). `geheelGetal` en het aantal staan daarnaast nog in `Definitie`, zoals
 * voorheen.
 */

/** De controls met een tekst als antwoord die een teken- of woordgrens kan tellen (R-12, R-13). */
const TEXTUAL: readonly Control[] = TEXT_CONTROLS
/** De controls met een getal als antwoord. */
const NUMERIC: readonly Control[] = ['number', 'currency', 'slider', 'rating']
/** De controls met een datum als antwoord; een `datetime` levert een tijdstip, het bereik gaat over de dag. */
const DATES: readonly Control[] = ['date', 'datetime']
/** De controls met één regel tekst waarin een identifier of een eigen vorm past. */
const FORMATTED: readonly Control[] = ['text', 'url', 'email', 'phone']

type FieldKind = Exclude<RuleKind, 'korteSectie' | 'zichtbaarBij' | 'zichtbaarAls'>

/** Of het antwoord van dit veld iets is wat de invuller invult (`RESPONSE_SHAPE`), en dus leeg kan zijn. */
function answers(field: DraftField): boolean {
  const shape = RESPONSE_SHAPE[field.control]
  return shape !== 'geen' && shape !== 'laag'
}

/** Of dit veld een aantal heeft om te begrenzen (R-42); een checkbox zonder opties is één losse instemming. */
function counts(field: DraftField): boolean {
  if (!isMultiValued(field.control)) return false
  return field.control !== 'checkbox' || ('opties' in field && field.opties !== undefined)
}

/** Per veldsoort: past hij op dit veld. Wat hier staat leest de evaluator ook zo (`check.ts`). */
const FITS: Record<FieldKind, (field: DraftField) => boolean> = {
  // Ook op een akkoord per persoon: daar betekent het minstens één akkoord dat nog geldt (eisenboek 12.56).
  verplicht: answers,
  maximumTekens: (field) => TEXTUAL.includes(field.control),
  maximumWoorden: (field) => TEXTUAL.includes(field.control),
  minimumWoorden: (field) => TEXTUAL.includes(field.control),
  verbodenTermen: (field) => TEXTUAL.includes(field.control),
  getalsbereik: (field) => NUMERIC.includes(field.control),
  // Alleen op een getalveld; dat eist `Definitie` ook.
  geheelGetal: (field) => field.control === 'number',
  datumbereik: (field) => DATES.includes(field.control),
  identifierFormaat: (field) => FORMATTED.includes(field.control),
  regexVorm: (field) => FORMATTED.includes(field.control) || field.control === 'textarea',
  // De poort op een akkoordveld (R-19, R-89). Op een gewone schakelaar klopt de naam van de regel niet.
  akkoordJa: (field) => field.control === 'toggle' && field.akkoord === true,
  minimumAantal: counts,
  maximumAantal: counts,
  bestandseisen: (field) => RESPONSE_SHAPE[field.control] === 'documenten',
}

/**
 * Of een regel van deze soort op dit veld past. Een zichtbaarheidsregel past op elk veld (R-33, D-20); of
 * haar voorwaarden bij hun bron passen is een andere vraag, en die stelt `visibilityGaps`.
 */
export function kindFitsField(kind: RuleKind, field: DraftField): boolean {
  if (kind === 'zichtbaarBij' || kind === 'zichtbaarAls') return true
  if (kind === 'korteSectie') return false
  return FITS[kind](field)
}

/** De soorten die een veld beheersen, afgeleid uit de takken van `Regel`. */
function kindsWithKey(key: 'doelVeld' | 'doelSectie'): readonly RuleKind[] {
  const out: RuleKind[] = []
  for (const branch of Rule.options) {
    const shape = branch.shape as Record<string, unknown> & { soort: { value?: unknown } }
    if (!(key in shape)) continue
    const kind = shape.soort.value
    if (typeof kind === 'string') out.push(kind as RuleKind)
  }
  return out
}

const ON_FIELD = kindsWithKey('doelVeld')
const ON_SECTION = kindsWithKey('doelSectie')

/**
 * De soorten die een sectie beheersen: haar eigen soorten plus de zichtbaarheid (D-20). Een nieuwe
 * zichtbaarheidsregel is altijd een `zichtbaarAls` (eisenboek 12.31); `zichtbaarBij` blijft geldig in wat
 * er al staat, maar wordt niet meer aangeboden.
 */
export const KINDS_ON_SECTION: readonly RuleKind[] = [...ON_SECTION, 'zichtbaarAls']

/**
 * De soorten die op dit veld passen, in de volgorde van het model.
 *
 * `regexVorm` zit erbij; het paneel zet hem onder Geavanceerd (`isAdvancedKind`). Of een soort er
 * al op staat, is kennis van het scherm: het paneel laat die weg, de poort niet.
 */
export function kindsForField(field: DraftField): readonly RuleKind[] {
  // Een zichtbaarheidsregel draagt `doel` en niet `doelVeld`, dus hij komt niet uit de afleiding
  // hierboven; hij hoort er wel bij, want zijn doel kan een veld zijn (R-33, D-20). Nieuw is altijd
  // `zichtbaarAls` (eisenboek 12.31).
  return [...ON_FIELD.filter((kind) => kindFitsField(kind, field)), 'zichtbaarAls']
}

/**
 * De soorten die een standaardveld mag dragen (D-48, D-49).
 *
 * Dezelfde lijst als voor een veld, min de zichtbaarheid. Een zichtbaarheidsregel hangt af van
 * een ánder veld (R-33), en dat andere veld staat niet in de bibliotheek: een standaardveld is
 * één veld dat op zichzelf staat, en bij het plaatsen zou zo'n regel naar niets wijzen. Het
 * schema van `Standaardveld` weigert hem dan ook; deze functie zorgt dat het beheerscherm hem
 * niet aanbiedt. Aanbieden wat het schema daarna afkeurt is dezelfde fout als een compartiment
 * aanbieden dat de poort weigert.
 */
export function kindsForDefaultField(field: DraftField): readonly RuleKind[] {
  return kindsForField(field).filter((kind) => kind !== 'zichtbaarBij' && kind !== 'zichtbaarAls')
}

/**
 * De soorten die het paneel onder Geavanceerd zet, dichtgeklapt (D-17, eisenboek 12.43).
 *
 * Eén soort: de regex-escape. Tot 1 november 2026 was hij voorbehouden aan beheer en zag een ronde-inrichter
 * hem niet; sinds die dag heeft iedereen met Wijzigen hem, maar niet in de gewone lijst. De vaste lijst doet
 * het gewone werk (U15), en een patroon is alleen met proberen te controleren (D-19). Wie wat mag is kennis
 * van de gebruiker en staat in de configurator; welke soort geavanceerd is, staat hier.
 */
export const ADVANCED_KINDS: readonly RuleKind[] = ['regexVorm']

/** Of deze soort onder Geavanceerd staat. */
export function isAdvancedKind(kind: RuleKind): boolean {
  return ADVANCED_KINDS.includes(kind)
}

/** Alle soorten, in de volgorde van het model; hier alleen om de import te binden. */
export const ALL_RULE_KINDS = RULE_KINDS

/** Of dit controltype een aantal kent; `MULTI_VALUED` uit het model, hier alleen doorgegeven. */
export function isMultiValued(control: string): control is MultiValued {
  return (MULTI_VALUED as readonly string[]).includes(control)
}
